import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/Home';
import GetJob from './components/GetJob';
import MyListings from './components/MyListings';
import CreateJob from './components/CreateJob';
import Profile from './components/Profile';
import PersonalInfo from './components/PersonalInfo';
import LoginPage from './components/LoginPage';
import RegistrationPage from './components/RegistrationPage';
import Refresh from './components/Refresh';
import Return from './components/Return';
import Applications from './components/MyApplications';
import JobInfo from './components/JobInfo';
import NotificationsPage from './components/NotificationsPage';
import AboutUs from './components/AboutUs';
import People from './components/People';
import ContactUs from './components/ContactUs';
import Wallet from './components/Wallet';
import SavedJobs from './components/SavedJobs';
import Chat from './components/Chat';
import './index.css';

function App() {
    return (
        <Router>
            <MainApp />
        </Router>
    );
}

/**
 * Route guard. Redirects unauthenticated users to /login. Waits for the initial
 * auth check (`authChecked`) to finish first, so a logged-in user refreshing a
 * protected page isn't bounced to login before their token resolves. The
 * attempted location is passed in `state.from` so login can send them back.
 */
function RequireAuth({ isLoggedIn, authChecked, children }) {
    const location = useLocation();
    if (!authChecked) {
        return (
            <div style={{ minHeight: '50vh', display: 'grid', placeItems: 'center', color: 'var(--ink-500)' }}>
                Loading…
            </div>
        );
    }
    if (!isLoggedIn) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return children;
}

// Actual logic here
function MainApp() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [showHeader, setShowHeader] = useState(false);
    // false until the initial token check resolves — gates the route guard so
    // logged-in users aren't redirected to /login before their session loads.
    const [authChecked, setAuthChecked] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Check localStorage first (remember me), then sessionStorage
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        const username = localStorage.getItem('username') || sessionStorage.getItem('username');

        if (token && refreshToken && username) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUserDetails(username, token).finally(() => setAuthChecked(true));
        } else {
            setAuthChecked(true);
        }
    }, []);

    useEffect(() => {
        setShowHeader(true);
    }, []);

    const fetchUserDetails = async (username, token) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/username/${username}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(response.data);
            setIsLoggedIn(true);
        } catch (error) {
            console.error('Error fetching user details:', error);
            logout();
        }
    };

    const login = (accessToken, refreshToken, username, rememberMe = false) => {
        const store = rememberMe ? localStorage : sessionStorage;
        store.setItem('accessToken', accessToken);
        store.setItem('refreshToken', refreshToken);
        store.setItem('username', username);
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        fetchUserDetails(username, accessToken);
    };

    const logout = () => {
        ['accessToken', 'refreshToken', 'username'].forEach(k => {
            sessionStorage.removeItem(k);
            localStorage.removeItem(k);
        });
        delete axios.defaults.headers.common['Authorization'];
        setIsLoggedIn(false);
        setUser(null);
    };

    const refreshAccessToken = async () => {
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (!refreshToken) {
            logout();
            return null;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh-token`, {}, {
                headers: { Authorization: `Bearer ${refreshToken}` },
            });

            if (response.data && response.data.access_token) {
                const store = localStorage.getItem('refreshToken') ? localStorage : sessionStorage;
                store.setItem('accessToken', response.data.access_token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
                return response.data.access_token;
            } else {
                logout();
                return null;
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            logout();
            return null;
        }
    };

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return axios(originalRequest);
                }
            }
            return Promise.reject(error);
        }
    );

    // Hide footer on get-job feed and the full-height chat inbox
    const hideFooter = location.pathname.startsWith('/get-job') || location.pathname.startsWith('/messages');

    return (
        <div className="flex flex-col min-h-screen">
            {showHeader && (
                <Header
                    isLoggedIn={isLoggedIn}
                    user={user}
                    logout={logout}
                />
            )}
            <div className="flex-grow pt-[72px]">
                <Routes>
                    {/* ── Public ── */}
                    <Route path="/" element={<Home user={user} />} />
                    <Route path="/get-job" element={<GetJob user={user} />} />
                    <Route path="/get-job/:jobId" element={<JobInfo />} />
                    <Route path="/login" element={<LoginPage onLogin={login} />} />
                    <Route path="/register" element={<RegistrationPage />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/people" element={<People />} />
                    <Route path="/contact" element={<ContactUs />} />
                    {/* Stripe Connect callbacks — left public so the redirect back from Stripe never breaks */}
                    <Route path="/refresh/:connectedAccountId" element={<Refresh />} />
                    <Route path="/return/:connectedAccountId" element={<Return />} />

                    {/* ── Protected: require login (redirect to /login otherwise) ── */}
                    <Route path="/create-job" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><CreateJob user={user} /></RequireAuth>} />
                    <Route path="/notifications" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><NotificationsPage /></RequireAuth>} />
                    <Route path="/my-listings" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><MyListings /></RequireAuth>} />
                    <Route path="/profile/:userId" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><Profile user={user} /></RequireAuth>} />
                    <Route path="/profile/:userId/personal-info" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><PersonalInfo user={user} /></RequireAuth>} />
                    <Route path="/profile/:userId/applications" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><Applications user={user} /></RequireAuth>} />
                    <Route path="/wallet" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><Wallet /></RequireAuth>} />
                    <Route path="/saved-jobs" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><SavedJobs /></RequireAuth>} />
                    <Route path="/messages" element={<RequireAuth isLoggedIn={isLoggedIn} authChecked={authChecked}><Chat /></RequireAuth>} />
                </Routes>
            </div>
            {!hideFooter && <Footer />}
        </div>
    );
}

export default App;
